/**
 * GovernShelf — baselines / scope-changes / approvals / audit_log.
 *
 * Phase 6 §"6-shelf reserve". Round 6 vocabulary discipline.
 */

import { useAuthStore } from "@/stores/auth";

import { ShelfCard } from "./ShelfCard";

// Same list NavPanel and ActivityRibbon use. The audit log is behind
// AdminPage, which redirects everyone else to "/" — so a card shown to a
// non-admin would bounce them home with no explanation, which is a worse
// answer than not offering it.
const ADMIN_ROLES = ["admin", "org_admin", "program_director"];

interface Props {
  projectId?: string | null;
}

export function GovernShelf({ projectId }: Props) {
  const base = projectId ? `/projects/${projectId}` : "";
  const user = useAuthStore((s) => s.user);
  const roles = (user?.roles ?? []).map((r) => r.toLowerCase());
  const isAdmin = user?.is_platform_admin === true
    || ADMIN_ROLES.some((r) => roles.includes(r));
  return (
    <div
      data-testid="govern-shelf"
      role="region"
      aria-label="Govern"
      className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2"
    >
      <ShelfCard
        to={`${base}/documents`}
        label="Documents"
        testId="govern-documents"
      />
      <ShelfCard
        to={`${base}/baselines`}
        label="Baselines"
        testId="govern-baselines"
      />
      <ShelfCard
        to={`${base}/change-control`}
        label="Scope Changes"
        testId="govern-scope-changes"
      />
      {/* ProgramGates. The card said /approvals, routed nowhere, so it was
          marked unavailable — while the surface a sponsor opens Govern to find
          was mounted the whole time at /projects/:projectId/gates. */}
      <ShelfCard
        to={`${base}/gates`}
        label="Approvals"
        testId="govern-approvals"
      />
      {/* Reports and Sponsor Update had no card (founder 6428293584 (B)). */}
      <ShelfCard
        to={`${base}/sponsor-update`}
        label="Sponsor Update"
        testId="govern-sponsor-update"
      />
      <ShelfCard
        to={`${base}/reports`}
        label="Reports"
        testId="govern-reports"
      />
      {/* Budget: contract price, payment milestones and (internal roles)
          cost / reserve / margin. Project-scoped, so only with a project. */}
      {projectId && (
        <ShelfCard
          to={`${base}/budget`}
          label="Budget"
          testId="govern-budget"
        />
      )}
      {/* ABSOLUTE, not ${base}/ — the audit log is org-level and mounted at
          /audit-log. There is no per-project audit route, so a project-scoped
          path would 404 exactly as /audit did.

          Shown to admins only: the route is behind AdminPage, which redirects
          everyone else to "/". A card that silently sends you home is worse
          than one that is not there. */}
      {isAdmin && (
        <ShelfCard
          to="/audit-log"
          label="Audit Log"
          testId="govern-audit"
        />
      )}
    </div>
  );
}
